/**
 * GL Budget vs Actual — the grid builder (CC-50252).
 *
 * `GLBudgetReport.jsx` was written against a payload CB has never sent, and
 * matched the real contract on *zero* points, so the report rendered with no
 * columns and no rows. Measured against a live capture (budget 4, Monthly,
 * 2026-01-01 → 2026-08-11) on 2026-08-11:
 *
 *   what the page read                | what CB actually sends
 *   ----------------------------------|--------------------------------------
 *   report.glBudgetSections           | report.gLBudgetSections   (capital L)
 *   report.glActualVsBudgetHeaders    | report.gLActualVsBudgetHeaders
 *   header.title || header.name       | header.columnHeader  ("Jan 2026")
 *   line.amounts || line.intervalAmounts | line.gLActualVsBudgetValues
 *
 * 0 of 76 line items carried `amounts`/`intervalAmounts`; 0 of 132 headers
 * carried `title`/`name`. The keys come from cb-api's `camelize()`, which
 * lowercases only the FIRST character — so CB's `GLBudgetSections` arrives as
 * `gLBudgetSections`. Never hand-write one of these; read it off a capture.
 *
 * ─── The shape, from V1 (AccountingGLActualVsBudgetReportView.js) ───────────
 *
 * V1 renders a two-row header (`:400-419`): row 1 is one `ColumnHeader` per
 * interval plus a closing "Totals"; row 2 repeats ACTUAL / BUDGET / OVER BUDGET
 * / % OF BUDGET beneath each. The shared grid has a single header row, so each
 * column is labelled "{interval} — {measure}" and the closing group "Total —
 * {measure}". Same cells, same order, one row of labels.
 *
 * Per account row (`:793-947`): the four measures from `gLActualVsBudgetValues[i]`
 * for each interval, then the four totals from `gLActualVsBudgetAccountTotals[0]`.
 * Then children recurse, then a "Total {account}" line from
 * `gLBudgetParentChildTotals` — whose LAST entry is the roll-up total and whose
 * earlier entries are per-interval (length is always intervals + 1).
 *
 * ─── Totals rows (spec 143) ─────────────────────────────────────────────────
 *
 * CB ends every section with a line item whose `accountName` is null (accountId
 * 0) — V1's "totals row" (`:758-785`). Its figures are `gLActualVsBudgetTotals`:
 * one entry per interval, then the Totals group. Its label is V1's: `totalsName`,
 * else `accountSubtypeName`, else "Total Income" / "Total Expenses" by `isIncome`
 * (an income row never reads `totalsName`). This builder used to flatten it as an
 * account, so every section closed with a BLANK row and Net Income was never
 * shown (measured 2026-10-08: Total Income 274,055.69, Total Expenses 137,440.26,
 * Net Income 136,615.43, Jan–Sep 2026, budget 4). It is now its section's footer.
 *
 * A section that carries ONLY totals rows — CB's Net Operating Income, Gross
 * Profit and Net Income, and the separate "Total Other Expense"-style subtype
 * sections that follow their accounts' section — is not a group of its own. V1
 * prints its line straight after the previous section's, with no heading
 * (`:426-431`), so its lines join the previous group's footer block (CC-50050).
 * A totals row with no figures at all (Gross Profit with no COGS) prints
 * nothing, as the P&L does — never a label over blanks.
 *
 * ─── A statement (spec 143) ─────────────────────────────────────────────────
 *
 * Rows carry the grid's `tree` exactly as the P&L's do (plStatement.js), so the
 * page renders in `statementMode`: never sorted (a sort interleaved parents,
 * children and "Total {parent}" lines — measured), and each parent rolls up to
 * CB's own roll-up figures, the Total line's — V1's collapse (`:448-477`).
 *

 * ⚠️ CB's own key casing is inconsistent between the two shapes and this is not
 * a typo here: the per-interval values use `quarterlyOverbud` / `quarterlyPerOfBud`
 * (lowercase b) while the account totals use `quarterlyOverBudTotal` /
 * `quarterlyPerOfBudTotal` (capital B). Both spellings are taken from a capture.
 *
 * Nothing is computed in this module. Every figure is a server value passed
 * through; the only decisions are which field belongs in which cell.
 */

import { flattenPLAccounts, ROW_TOTAL } from './plAccountTree'
import { treeRefusal, treeId } from './plStatement'
import { withAliasKeys } from '@components/reports/aliasedGridColumns'

/** The grid column that carries the roll-up toggle and the indent. */
export const GLB_TREE_COLUMN = 'account'

/** V1 suppresses the heading for these computed sections (`:426`). */
export const UNLABELLED_SECTIONS = ['Gross Profit', 'Net Income', 'Net Operating Income']

/**
 * The three interval families CB can populate. Exactly one is filled per run;
 * the others come back as empty strings, so the family is chosen from the DATA
 * rather than from a filter string or a guessed enum value.
 */
const VALUE_FIELDS = {
  monthly: ['monthlyActual', 'monthlyBudget', 'monthlyOverBudget', 'monthlyPercentOfBudget'],
  quarterly: ['quarterlyActual', 'quarterlyBudget', 'quarterlyOverbud', 'quarterlyPerOfBud'],
  yearly: ['yearlyActual', 'yearlyBudget', 'yearlyOverbud', 'yearlyPerOfBud'],
}
const TOTAL_FIELDS = {
  monthly: ['monthlyActualTotal', 'monthlyBudgetTotal', 'monthlyOverBudTotal', 'monthlyPerOfBudTotal'],
  quarterly: ['quarterlyActualTotal', 'quarterlyBudgetTotal', 'quarterlyOverBudTotal', 'quarterlyPerOfBudTotal'],
  yearly: ['yearlyActualTotal', 'yearlyBudgetTotal', 'yearlyOverBudTotal', 'yearlyPerOfBudTotal'],
}
/** Parent roll-up rows use one flat shape regardless of interval. */
const PARENT_FIELDS = ['actualTotal', 'budgetTotal', 'overBudgetTotal', 'perOfBudgetTotal']

export const MEASURES = ['Actual', 'Budget', 'Over Budget', '% of Budget']
/** The 4th measure is a percentage; the other three are money. */
const PERCENT_INDEX = 3

/** Money, matching the other statements: blank for zero, negatives in parens. */
export const fmtMoney = (v) => {
  const n = Number(v ?? 0)
  if (!Number.isFinite(n) || n === 0) return ''
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `($${s})` : `$${s}`
}

/**
 * CB sends the percentage already scaled (`"606.450000"` means 606.45%). V1
 * divides by 100 and formats with a percent formatter that multiplies back, so
 * the number on screen is the raw one — don't rescale it.
 */
export const fmtPercent = (v) => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  return `${n.toFixed(2)}%`
}

export const esc = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/** CB serializes a section name as a struct on some payloads; prefer strings. */
export const sectionLabel = (sec) =>
  (typeof sec?.accountName === 'string' && sec.accountName)
  || sec?.sectionName?.description
  || sec?.sectionName?.name
  || (typeof sec?.sectionName === 'string' ? sec.sectionName : '')

/** Read the sections/headers off the payload, tolerating both spellings so a
 *  future cb-api normalisation cannot silently blank the report again. */
export const budgetSections = (report) => report?.gLBudgetSections ?? report?.glBudgetSections ?? []
export const budgetHeaders = (report) => report?.gLActualVsBudgetHeaders ?? report?.glActualVsBudgetHeaders ?? []

/**
 * Which interval family carries data. Picked by inspecting real values so the
 * builder never depends on the interval filter string or on CB's enum numbering.
 */
export function detectFamily(sections = []) {
  const filled = (fields) => {
    for (const sec of sections) {
      for (const li of sec?.lineItems || []) {
        for (const v of li?.gLActualVsBudgetValues || []) {
          if (fields.some((f) => v?.[f] !== '' && v?.[f] != null)) return true
        }
      }
    }
    return false
  }
  if (filled(VALUE_FIELDS.monthly)) return 'monthly'
  if (filled(VALUE_FIELDS.quarterly)) return 'quarterly'
  if (filled(VALUE_FIELDS.yearly)) return 'yearly'
  return 'monthly'
}

const cell = (columnId, raw, isPercent) => {
  const n = Number(raw)
  const c = {
    columnId,
    displayValue: isPercent ? fmtPercent(raw) : fmtMoney(raw),
  }
  if (raw !== '' && raw != null && Number.isFinite(n)) c.dataValue = n
  return c
}

/**
 * A SECTION total's cell (spec 143). Like `cell`, except a zero CB sent prints
 * "$0.00": a blank Net Income reads as missing (the P&L's fix, spec 127 / CC-53903).
 * An absent figure — CB sends "" for "no budget" — stays blank, so nothing is
 * printed that CB did not send. (Parent "Total {account}" lines keep `cell`: CB
 * sends their figures as NUMBERS, where "no budget" already arrives as 0, so a
 * printed $0.00 there could not be told from a real one.)
 */
const totalCell = (columnId, raw, isPercent) => {
  const c = cell(columnId, raw, isPercent)
  if (!isPercent && c.dataValue === 0) c.displayValue = '$0.00'
  return c
}

/** V1 `:759`: "It's a totals row" — the line item CB ends a section with. */
export const isTotalsRow = (li) => li != null && typeof li === 'object' && li.accountName == null

const nonBlank = (s) => (typeof s === 'string' && s.trim() !== '' ? s : null)

/** V1 `:758-785`, verbatim in its precedence: an income row never reads `totalsName`. */
export const totalsRowLabel = (li) => {
  const subtype = nonBlank(li?.accountSubtypeName)
  if (li?.isIncome) return subtype || 'Total Income'
  return nonBlank(li?.totalsName) || subtype || 'Total Expenses'
}

/**
 * Build the grid report for GL Budget vs Actual.
 *
 * @param {object} report the `data.report` payload
 * @returns {{columns: Array, reportData: Array, reportFooterCells: Array,
 *   treeColumnId: string, rollupUnavailable?: string}}
 */
export function buildGlBudgetGrid(report) {
  const sections = budgetSections(report)
  const headers = budgetHeaders(report)
  const family = detectFamily(sections)
  const valueFields = VALUE_FIELDS[family]
  const totalFields = TOTAL_FIELDS[family]

  // CC-50291: `aliasKey` opts each column into per-firm renaming. These
  // columnIds are STRUCTURAL, not positional — `i{i}m{m}` names interval i's
  // measure m, so it survives hiding or reordering a column, unlike an array
  // index. So the columnId is itself the stable identity and doubles as the
  // rename key. (Adding or removing a budget interval does change how many
  // columns exist, and an alias on a dropped interval simply stops applying;
  // that is the same behaviour a renamed CB column already gets.)
  const columns = withAliasKeys([
    { columnId: 'account', label: 'Account', visible: true, columnAlignment: 'LEFT' },
    ...headers.flatMap((h, i) =>
      MEASURES.map((measure, m) => ({
        columnId: `i${i}m${m}`,
        label: `${String(h?.columnHeader || `Period ${i + 1}`)} — ${measure}`,
        visible: true,
        columnAlignment: 'RIGHT',
      }))
    ),
    // V1's closing "Totals" group (`:389`, `:407`).
    ...MEASURES.map((measure, m) => ({
      columnId: `tm${m}`,
      label: `Total — ${measure}`,
      visible: true,
      columnAlignment: 'RIGHT',
    })),
  ])

  const intervalCells = (values = []) =>
    headers.flatMap((_, i) => {
      const v = values[i]
      return MEASURES.map((_m, m) => cell(`i${i}m${m}`, v?.[valueFields[m]], m === PERCENT_INDEX))
    })

  const accountTotalCells = (li) => {
    const t = (li?.gLActualVsBudgetAccountTotals || [])[0]
    return MEASURES.map((_m, m) => cell(`tm${m}`, t?.[totalFields[m]], m === PERCENT_INDEX))
  }

  // A parent's roll-up line. `gLBudgetParentChildTotals` runs one entry per
  // interval and closes with the grand total, so its last element is the Totals
  // group and the earlier ones line up with the interval columns.
  const parentTotalCells = (li) => {
    const rows = li?.gLBudgetParentChildTotals || []
    const grand = rows.length > 0 ? rows[rows.length - 1] : null
    return [
      ...headers.flatMap((_, i) => {
        const r = rows[i]
        return MEASURES.map((_m, m) => cell(`i${i}m${m}`, r?.[PARENT_FIELDS[m]], m === PERCENT_INDEX))
      }),
      ...MEASURES.map((_m, m) => cell(`tm${m}`, grand?.[PARENT_FIELDS[m]], m === PERCENT_INDEX)),
    ]
  }

  // A section totals row's line (spec 143): `gLActualVsBudgetTotals` lines up with
  // the intervals and closes with the Totals group, exactly like a parent's
  // `gLBudgetParentChildTotals` (V1 `:847-…`). Null when CB sent no entries.
  const totalsRowCells = (li) => {
    const entries = Array.isArray(li?.gLActualVsBudgetTotals) ? li.gLActualVsBudgetTotals : []
    if (entries.length === 0) return null
    const grand = entries[entries.length - 1]
    return [
      { columnId: 'account', displayValue: `<span class="font-medium">${esc(totalsRowLabel(li))}</span>` },
      ...headers.flatMap((_, i) => MEASURES.map((_m, m) => totalCell(`i${i}m${m}`, entries[i]?.[totalFields[m]], m === PERCENT_INDEX))),
      ...MEASURES.map((_m, m) => totalCell(`tm${m}`, grand?.[totalFields[m]], m === PERCENT_INDEX)),
    ]
  }

  // Same traversal as the P&L (CC-50249): account, descendants, then the
  // "Total {parent}" line — V1 recurses ChildLines identically here (`:963`). The
  // totals rows are not accounts and never enter the tree.
  const flats = sections.map((sec) => flattenPLAccounts((sec?.lineItems || []).filter((li) => !isTotalsRow(li))))
  // The P&L's duplicate-id guard (spec 127 §4.2): a colliding id would roll up — and
  // hide — the wrong rows, so a report with one carries no tree at all.
  const refusal = treeRefusal(flats)
  const withTree = refusal == null
  const hasParent = flats.some((flat) => flat.some((r) => r.kind === ROW_TOTAL))

  const reportData = []
  sections.forEach((sec, si) => {
    const flat = flats[si]
    // Rollable = the flattened tree gave the account a following Total line —
    // keyed on the item itself, never on CB's `isParent` (as the P&L).
    const closes = new Set(flat.filter((r) => r.kind === ROW_TOTAL).map((r) => r.item))
    const rows = flat.map(({ item: li, depth, kind, parentId }) => {
      const isTotal = kind === ROW_TOTAL
      const name = esc(li.accountName || li.name || '')
      const rowLabel = isTotal ? `Total ${name}` : name
      // With a tree the GRID indents from `tree.depth` (and exports carry the depth
      // as a number). Without one, the label keeps its own indent so the hierarchy
      // still reads.
      const indented = !withTree && depth > 0
        ? `<span style="padding-left:${depth * 16}px">${rowLabel}</span>`
        : rowLabel
      const row = {
        isGroup: false,
        dataCells: [
          {
            columnId: 'account',
            displayValue: isTotal ? `<span class="font-medium">${indented}</span>` : indented,
          },
          ...(isTotal
            ? parentTotalCells(li)
            : [...intervalCells(li.gLActualVsBudgetValues), ...accountTotalCells(li)]),
        ],
      }
      if (withTree) {
        const tree = {
          id: treeId(li.accountId),
          parentId: parentId != null ? treeId(parentId) : null,
          depth,
          kind: isTotal ? 'close' : 'node',
        }
        // A rolled parent shows what its Total line shows — CB's roll-up — under
        // its own name: V1's collapsed row (`:448-477`).
        if (!isTotal && closes.has(li)) {
          tree.rolledCells = [{ columnId: 'account', displayValue: name }, ...parentTotalCells(li)]
        }
        row.tree = tree
      }
      return row
    })
    const lines = (sec?.lineItems || []).filter(isTotalsRow).map(totalsRowCells).filter(Boolean)
    const previous = reportData[reportData.length - 1]
    if (rows.length === 0 && previous) {
      // A totals-only section (Net Income, "Total Other Expense", …): its lines
      // close the previous section's block, where V1 prints them, headingless.
      if (lines.length > 0) {
        const block = [...(previous.groupFooterRows || (previous.groupFooterCells ? [previous.groupFooterCells] : [])), ...lines]
        previous.groupFooterCells = block[0]
        if (block.length > 1) previous.groupFooterRows = block
      }
      return
    }
    // Nothing to show at all: a genuinely empty section was never worth a heading.
    if (rows.length === 0 && lines.length === 0) return
    const group = { isGroup: true, groupHeaderContent: esc(sectionLabel(sec)), dataRows: rows }
    if (lines.length > 0) group.groupFooterCells = lines[0]
    // CC-50050's multi-line block only when there IS a second line.
    if (lines.length > 1) group.groupFooterRows = lines
    reportData.push(group)
  })

  // GUARDRAIL: CB sends no REPORT-level totals for this statement — its totals are
  // per section, above — and Actual / Budget / Over-Budget / % columns are not
  // additive across accounts (the % one least of all). Fabricating a report footer
  // would invent figures, so there is none.
  return {
    columns,
    reportData,
    reportFooterCells: [],
    treeColumnId: GLB_TREE_COLUMN,
    // Said only when something WOULD have rolled up: with no parent nothing is lost.
    ...(refusal && hasParent ? { rollupUnavailable: refusal } : {}),
  }
}

export default buildGlBudgetGrid
